import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useCoachMode } from "./CoachModeContext";
import { fetchCoachCircle, logCoachContact, logCoachGroupContact } from "./coachServices";
import { activeTeamOf, circleFilter, teamKey, teamNameOf } from "./coachUtils";
import { CoachPhoneHead, LockedCard, TeamSwitch } from "./coachPieces";
import { circleEmptyText, contactLogFor, groupButton, groupDraft, groupRecipients, outreachDraft } from "./circle/circleModel";
import { CircleFilters, CircleIntroCard } from "./circle/circlePieces";
import CircleContactsTable from "./circle/CircleContactsTable";
import RosterModals from "./RosterModals";
import { Btn, ErrorState, Loading, PageHeader } from "../../ForBrands/ui/BrandUI";
import { usePhoneTheme } from "../../ForBrands/useIsPhone";

// Coach Mode · My Circle (Figma 04 · My Circle): the roster athletes on
// CACHE and the guardians and agents in their Circles, with minors who have
// no guardian on file flagged for a consent request. Message and Message
// group open a mail draft and log the contact, so Last contact stays real.
//
// On phones (Figma COACH MODE V2 mobile, 04 · My Circle 2412:1393) the page
// keeps the same sections in the same order: the coach phone head (no
// eyebrow, the 56px team switch, Manage roster and Invite someone sharing a
// row, the full-width Message group, D-c1), then the intro card, the chips
// and the contact cards 18px apart. The pieces switch themselves
// (circlePieces.js, CircleContactsTable.js). Brands stay out of the
// subtitle, as on the web (D-c5).

const GROUP = "group"; // busy key of "Message group"

const SUBTITLE = "Reach your athletes and the people who support them: guardians and agents.";

const CoachCirclePage = () => {
  const navigate = useNavigate();
  const phone = usePhoneTheme();
  const { selection, absorbSelector, selectTeam } = useCoachMode();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [busyId, setBusyId] = useState(null);
  const [modal, setModal] = useState(null); // "invite" | "roster"

  const load = async (team, season) => {
    setLoading(true);
    try {
      const payload = await fetchCoachCircle(team, season);
      setData(payload);
      absorbSelector(payload.selector, team);
      setError(null);
    } catch (err) {
      setError(err.message || "Could not load your Circle");
    } finally {
      setLoading(false);
    }
  };
  const reload = () => load(selection.team, selection.season);

  // Keyed on team AND league: one program can be several squads that share
  // a team id (17U / 16U / 15U), one per league.
  const selectedTeam = teamKey(selection.team);
  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTeam, selection.season]);

  const rows = useMemo(() => circleFilter(data?.contacts, filter), [data, filter]);
  const recipients = useMemo(() => groupRecipients(rows), [rows]);
  // The team the drafts name, by the one display rule ("Oakland Soldiers 16U").
  const teamName = teamNameOf(data?.team);

  // Log first, so Last contact is right, then open the draft and refresh.
  const reachOut = async (busyKey, logContact, draft) => {
    setBusyId(busyKey);
    try {
      await logContact();
      window.location.href = draft;
      await reload();
    } catch (err) {
      toast.error(err.message || "Could not log the contact");
    } finally {
      setBusyId(null);
    }
  };
  const reach = (row) => reachOut(row.id, () => logCoachContact(contactLogFor(row)), outreachDraft(row, teamName));
  const messageGroup = () =>
    reachOut(GROUP, () => logCoachGroupContact(selection.team, selection.season, recipients), groupDraft(recipients, teamName));

  if (error && !data) {
    return (
      <div className="flex flex-col items-start gap-3 py-10">
        <ErrorState>{error}</ErrorState>
        <Btn variant="secondary" onClick={reload}>
          Try again
        </Btn>
      </div>
    );
  }
  if (!data) return <Loading />;

  const activeTeam = activeTeamOf(selection);
  const group = groupButton(recipients.length, { locked: data.locked });
  const groupBusy = busyId === GROUP;
  const groupLabel = groupBusy ? "Opening..." : "Message group";
  const teamSwitch = <TeamSwitch selection={selection} onSelect={selectTeam} disabled={loading} />;

  return (
    <div>
      {phone ? (
        <CoachPhoneHead
          title="My Circle"
          sub={SUBTITLE}
          switcher={teamSwitch}
          secondary={[activeTeam && { label: "Manage roster", onClick: () => setModal("roster") }, { label: "Invite someone", onClick: () => setModal("code") }]}
          primary={{ label: groupLabel, onClick: messageGroup, disabled: !group.enabled || groupBusy, title: group.title }}
        />
      ) : (
        <PageHeader
          align="center"
          eyebrow="Coach Mode"
          title="My Circle"
          subtitle={SUBTITLE}
          actions={
            <div className="flex flex-wrap items-center gap-6">
              {teamSwitch}
              {activeTeam && (
                <Btn variant="secondary" onClick={() => setModal("roster")}>
                  Manage roster
                </Btn>
              )}
              <span className="flex items-center gap-[10px]">
                <Btn variant="secondary" onClick={() => setModal("code")}>
                  Invite someone
                </Btn>
                <Btn onClick={messageGroup} disabled={!group.enabled || groupBusy} title={group.title}>
                  {groupLabel}
                </Btn>
              </span>
            </div>
          }
        />
      )}

      <div className={phone ? "flex flex-col gap-[18px] pt-[18px]" : "flex flex-col gap-4 pt-7"}>
        {error && <ErrorState>{error}</ErrorState>}
        {data.locked ? (
          <LockedCard title="My Circle locked">Your athletes, their guardians and their agents appear here once CACHE verifies you as this team's coach.</LockedCard>
        ) : (
          <>
            <CircleIntroCard />
            <CircleFilters value={filter} counts={data.counts} onChange={setFilter} />
            <CircleContactsTable
              rows={rows}
              busyId={busyId}
              onAction={reach}
              onOpenAthlete={(row) => navigate(`/business/athlette/profile/${row.athleteId}`)}
              emptyText={circleEmptyText(filter)}
            />
          </>
        )}
      </div>

      <RosterModals modal={modal} onClose={() => setModal(null)} team={activeTeam} players={data.roster} onChanged={reload} codeTitle="Invite someone to your roster" />
    </div>
  );
};

export default CoachCirclePage;
